import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Bell, AtSign } from "lucide-react";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const panelRef = useRef();

  const load = () => api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const close = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/mark-read"); } catch (e) {}
      setUnread(0);
    }
  };

  const openItem = (n) => {
    setOpen(false);
    navigate(`/designs/${n.design_id}`);
  };

  return (
    <div ref={panelRef} className="relative">
      <button data-testid="notification-bell" onClick={toggle} title="Notifications"
        className="relative p-2 rounded-md hover:bg-zinc-100 text-zinc-500 transition-colors">
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 bg-zinc-950 text-white text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center font-medium">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="fixed left-64 top-3 w-96 bg-white border border-zinc-200 rounded-lg shadow-lg z-[60] overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
            <AtSign size={14} className="text-zinc-400" />
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Mentions</p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <p className="p-6 text-sm text-zinc-400 text-center" data-testid="notification-empty">No mentions yet — teammates can @you on any design.</p>}
            {items.map((n) => (
              <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50 transition-colors ${!n.read ? "bg-zinc-50/80" : ""}`}>
                <p className="text-sm">
                  <span className="font-medium">{n.actor_name}</span>
                  <span className="text-zinc-500"> mentioned you on </span>
                  <span className="font-medium">{n.design_title}</span>
                </p>
                <p className="text-xs text-zinc-500 mt-1 line-clamp-2">"{n.text}"</p>
                <p className="text-[11px] text-zinc-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
